import { useEffect, useState } from 'react';
import { Card } from '../../components/ui';
import { api, ApiError } from '../../lib/api';

interface QrInfo {
  hasToken: boolean;
  url?: string;
  qrDataUrl?: string;
  tokenCreatedAt?: string | null;
}

/**
 * QR / capability-URL management for a week. The URL is only shown to staff.
 * Supports generate/regenerate/revoke, copy, fullscreen, PNG download, and a
 * clean branded printable page.
 */
export function QrPanel({
  weekId,
  weekNumber,
  weekName,
}: {
  weekId: string;
  weekNumber: number;
  weekName: string;
}) {
  const [info, setInfo] = useState<QrInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  async function load() {
    try {
      setInfo(await api.get<QrInfo>(`/admin/weeks/${weekId}/qr`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Unable to load QR.');
    }
  }

  useEffect(() => {
    load();
  }, [weekId]);

  async function generate(regenerate: boolean) {
    if (regenerate && !confirm('Regenerate the URL? The old QR code will stop working immediately.'))
      return;
    setBusy(true);
    setError('');
    try {
      const res = await api.post<{ url: string; qrDataUrl: string }>(
        `/admin/weeks/${weekId}/qr/generate`,
      );
      setInfo({ hasToken: true, url: res.url, qrDataUrl: res.qrDataUrl });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Unable to generate QR.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!confirm('Revoke the current URL? Players will no longer be able to open the pick form.'))
      return;
    setBusy(true);
    try {
      await api.post(`/admin/weeks/${weekId}/qr/revoke`);
      setInfo({ hasToken: false });
    } finally {
      setBusy(false);
    }
  }

  function copy() {
    if (!info?.url) return;
    navigator.clipboard.writeText(info.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  function printPage() {
    if (!info?.qrDataUrl) return;
    const w = window.open('', '_blank', 'width=800,height=1000');
    if (!w) return;
    // A clean, branded printable page. The secret token is not shown in text.
    w.document.write(`<!doctype html><html><head><meta charset="utf-8">
      <title>Lost Grove Pick 'Em — Week ${weekNumber}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Roboto:wght@400;700&display=swap');
        body{margin:0;font-family:Roboto,sans-serif;color:#161616;text-align:center;
          display:flex;min-height:100vh;flex-direction:column;align-items:center;justify-content:center;padding:40px;box-sizing:border-box}
        h1{font-family:'Bebas Neue',sans-serif;font-size:72px;margin:0;color:#3C462C;letter-spacing:1px}
        h2{font-family:'Bebas Neue',sans-serif;font-size:120px;margin:0;color:#E1AB47;line-height:1}
        p{font-size:24px;margin:8px 0 0}
        img{width:min(70vw,480px);height:auto;margin:24px 0}
        .tag{font-size:18px;color:#6B6B6B;margin-top:16px}
      </style></head><body>
      <h1>Lost Grove Pick 'Em</h1>
      <h2>Week ${weekNumber}</h2>
      <p>Scan to make your picks</p>
      <img src="${info.qrDataUrl}" alt="QR code" />
      <p class="tag">${weekName} · Lost Grove Brewing</p>
      <script>window.onload=function(){setTimeout(function(){window.print()},300)}</script>
      </body></html>`);
    w.document.close();
  }

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-2xl uppercase text-ink">QR Code / Pick URL</h3>
        {info?.hasToken && <span className="chip bg-success/15 text-success">Active</span>}
      </div>
      {error && <p className="mb-2 text-sm text-danger">{error}</p>}

      {!info ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : !info.hasToken ? (
        <div>
          <p className="mb-3 text-sm text-muted">
            No QR code yet. Generate one and display it at the brewery so players can scan in.
          </p>
          <button className="btn-primary" onClick={() => generate(false)} disabled={busy}>
            {busy ? 'Generating…' : 'Generate QR code'}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-4 sm:flex-row">
          <img
            src={info.qrDataUrl}
            alt="Weekly pick QR code"
            className="mx-auto h-40 w-40 rounded border border-line bg-white p-2"
          />
          <div className="flex-1 space-y-3">
            <div>
              <div className="label">Capability URL (staff only — do not post publicly)</div>
              <div className="flex gap-2">
                <input readOnly className="input font-mono text-xs" value={info.url ?? ''} />
                <button className="btn-outline" onClick={copy}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button className="btn-outline" onClick={() => setFullscreen(true)}>
                Fullscreen
              </button>
              <a
                className="btn-outline"
                href={`/api/admin/weeks/${weekId}/qr.png`}
                download={`lost-grove-pickem-week-${weekNumber}.png`}
              >
                Download PNG
              </a>
              <button className="btn-outline" onClick={printPage}>
                Print page
              </button>
              <button className="btn-outline" onClick={() => generate(true)} disabled={busy}>
                Regenerate
              </button>
              <button className="btn-ghost text-danger" onClick={revoke} disabled={busy}>
                Revoke
              </button>
            </div>
          </div>
        </div>
      )}

      {fullscreen && info?.qrDataUrl && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-grove p-8 text-center text-white"
          onClick={() => setFullscreen(false)}
          role="dialog"
          aria-label="Fullscreen QR code"
        >
          <h1 className="font-display text-5xl uppercase">Lost Grove Pick&nbsp;’Em</h1>
          <div className="my-2 font-display text-7xl uppercase text-amber">Week {weekNumber}</div>
          <p className="text-xl">Scan to make your picks</p>
          <img
            src={info.qrDataUrl}
            alt="QR code"
            className="my-6 h-[min(60vh,60vw)] w-[min(60vh,60vw)] rounded-lg bg-white p-4"
          />
          <p className="text-sm text-white/70">Tap anywhere to close</p>
        </div>
      )}
    </Card>
  );
}
